import { Image } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import { useEffect, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react'

/*
 * ⚠️ 이 파일은 관리자 BO(apps/backoffice)와 Dealer BO(apps/dealer-backoffice)에 똑같은 사본으로 있다.
 * 한쪽을 고치면 같은 커밋에서 다른 쪽도 같은 내용으로 고친다 — 두 파일이 한 글자라도 다르면
 * apps/backoffice/src/components/ThumbnailImage.sync.test.ts가 실패한다.
 * 구조와 이유: ixc_md/가이드/상품 이미지 팝업 공용 구조.md
 */

/** 이미지를 못 불러왔을 때 자리를 지키는 회색 사각형 (반품 사진 등 antd Image를 직접 쓰는 곳도 이 값을 쓴다) */
export const FALLBACK_IMAGE =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiBmaWxsPSIjZjBmMGYwIi8+PC9zdmc+'

/** 행 클릭(상세 이동)까지 번지지 않게 막는다. */
function stopRowClick(event: MouseEvent<HTMLSpanElement>): void {
  event.stopPropagation()
}

export interface ThumbnailImageProps {
  /** 썸네일 이미지 URL. 비어 있으면 빈 자리표시자만 그린다 */
  src?: string | null
  /** 가로·세로 크기(px). width/height를 따로 줄 때는 생략한다 */
  size?: number
  /** 가로 크기. `'100%'`처럼 문자열이면 세로를 생략해 원본 비율로 그린다 */
  width?: number | string
  height?: number | string
  alt?: string
  /** 팝업에서 보여줄 원본 URL. 생략하면 `src`를 그대로 쓴다 */
  previewSrc?: string
  /** 이미지 자체에 덧붙일 스타일 */
  style?: CSSProperties
  /** 썸네일 위에 겹치는 안내(마스크). 기본은 돋보기 아이콘, `false`면 표시하지 않는다 */
  mask?: ReactNode
  /** true면 클릭해도 팝업을 열지 않는다 */
  disabled?: boolean
  'data-testid'?: string
}

/**
 * 상품 이미지 공용 썸네일 — 상품 이미지 팝업을 여는 유일한 경로.
 *
 * 클릭하면 antd Image의 전체 화면 팝업이 열린다. 행 전체에 이동(onRow onClick 등)이 걸려 있어도
 * 썸네일 클릭은 여기서 멈추므로, 썸네일=팝업 / 나머지=이동으로 동작한다 (2026-09-10 사용자 결정).
 * 여러 장을 좌우로 넘겨보려면 {@link ThumbnailImageGroup}으로 감싼다.
 *
 * 상품 이미지를 그리는 곳은 이 컴포넌트만 쓰고 antd `Image`의 `preview`를 각자 두지 않는다.
 */
export function ThumbnailImage({
  src,
  size = 32,
  width,
  height,
  alt,
  previewSrc,
  style,
  mask,
  disabled = false,
  'data-testid': dataTestId,
}: ThumbnailImageProps) {
  // 불러오지 못한 URL. URL과 함께 기억하므로 src가 바뀌면 다시 불러온다. 깨진 이미지는 팝업으로 열지 않는다(앱과 같은 규칙)
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const failed = Boolean(src) && src === failedSrc
  const previewDisabled = disabled || failed
  // antd Image는 실패를 따로 확인해 fallback으로 바꾸므로 <img>의 onError가 오지 않을 수 있다 — 같은 방식으로 확인한다
  useEffect(() => {
    if (!src) return
    let current = true
    const probe = new window.Image()
    probe.addEventListener('error', () => {
      if (current) setFailedSrc(src)
    })
    probe.src = src
    return () => {
      current = false
    }
  }, [src])
  const resolvedWidth = width ?? size
  // 가로만 지정하면 세로는 원본 비율을 따른다.
  const resolvedHeight = height ?? (width === undefined ? size : undefined)

  if (!src) {
    return (
      <div
        data-testid={dataTestId}
        style={{
          width: resolvedWidth,
          height: resolvedHeight ?? size,
          borderRadius: 4,
          background: '#f0f0f0',
          ...style,
        }}
      />
    )
  }

  return (
    <span
      onClick={stopRowClick}
      style={{
        display: 'inline-flex',
        lineHeight: 0,
        width: typeof resolvedWidth === 'string' ? resolvedWidth : undefined,
      }}
    >
      <Image
        src={src}
        alt={alt}
        width={resolvedWidth}
        height={resolvedHeight}
        fallback={FALLBACK_IMAGE}
        style={{
          objectFit: 'cover',
          borderRadius: 4,
          cursor: previewDisabled ? 'default' : 'zoom-in',
          ...style,
        }}
        preview={
          previewDisabled ? false : { src: previewSrc ?? src, mask: mask ?? <EyeOutlined /> }
        }
        data-testid={dataTestId}
      />
    </span>
  )
}

/**
 * 안에 있는 {@link ThumbnailImage}들을 팝업에서 좌우로 넘겨볼 수 있게 묶는다 (상품 상세 이미지 갤러리 등).
 */
export function ThumbnailImageGroup({ children }: { children: ReactNode }) {
  return <Image.PreviewGroup>{children}</Image.PreviewGroup>
}
